import { useRef } from 'react';
import { Tag } from 'antd';
import { ColumnsType, TableProps } from 'antd/es/table';

import { ValidateAccessEligibilityResult } from '@app/domain-bundle-interfaces';
import { SubscriptionWithChildren } from '@app/domain-subscription-interfaces';

import { SubscriptionAccessStatusRenderer } from '../../../admin/reports/access-status';
import { useEligibilityTag, useValidateAccessEligibility } from '../../../bundles';
import { ApproverTable, EligibilityPopover, RetryButton } from '../../modals';
import { useSubscriptionPermissions } from '../rules/use-subscription-permissions';

export const useRetryAccessesTable = (subscription: SubscriptionWithChildren, refetch: () => void) => {
  const { data, isFetching } = useValidateAccessEligibility({
    bundleId: subscription.bundleId,
    requesterEnterpriseId: subscription.user.enterpriseId,
  });

  const { getProps: getBooleanTagProps } = useEligibilityTag();
  const { canRetryAccess } = useSubscriptionPermissions();
  const enableRetryAccess = canRetryAccess(subscription);
  const tagRef = useRef<HTMLElement>(null);

  const columns: ColumnsType<ValidateAccessEligibilityResult> = [
    {
      title: 'Subscribed',
      width: '50px',
      render: (_, record) => {
        const subscribed = !!subscription.accesses.find((x) => x.bundleAccessId === record.bundleAccessId)?.subscribed;
        return <Tag {...getBooleanTagProps(subscribed)} />;
      },
    },
    {
      title: 'Eligible',
      width: '50px',
      dataIndex: 'isEligible',
      render: (isEligible, record, index) =>
        record.messages &&
        typeof record.isEligible === 'boolean' && (
          <EligibilityPopover messages={record.messages}>
            <Tag ref={index === 0 ? tagRef : undefined} {...getBooleanTagProps(isEligible)} />
          </EligibilityPopover>
        ),
    },
    { title: 'Name', dataIndex: 'accessName' },
    { title: 'Source', dataIndex: 'accessSource' },
    {
      title: 'Status',
      width: '50px',
      render: (_, record) => {
        const target = subscription.accesses.find((x) => x.bundleAccessId === record.bundleAccessId);
        return (
          target && (
            <SubscriptionAccessStatusRenderer
              access={target.bundleAccess.access}
              status={target.status}
              user={subscription.user}
            />
          )
        );
      },
    },
    {
      title: 'Status Description',
      render: (_, record) => {
        const target = subscription.accesses.find((x) => x.bundleAccessId === record.bundleAccessId);
        return target?.statusDescription;
      },
    },
  ];

  if (enableRetryAccess) {
    columns.unshift({
      title: 'Action',
      width: '100px',
      render: (_, record) => {
        const target = subscription.accesses.find((x) => x.bundleAccessId === record.bundleAccessId);

        return target?.subscribed && <RetryButton subscriptionAccessId={target.id} onReload={() => refetch()} />;
      },
    });
  }

  const pendingRows = subscription.accesses.filter((x) => x.status === 'PENDING').map((x) => x.bundleAccessId);

  const getProps = (): TableProps => ({
    columns,
    dataSource: data,
    expandable: {
      expandedRowRender: (record) => {
        return (
          <ApproverTable
            accessSource={record.accessSource}
            externalId={record.externalId}
            name={record.accessName}
            style={{ marginLeft: -40 }}
          />
        );
      },
      defaultExpandedRowKeys: pendingRows,
    },
    loading: isFetching,
    pagination: false,
    rowKey: 'bundleAccessId',
    size: 'small',
  });

  return { getProps };
};
